import { useState, useEffect, useMemo } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, LayoutGrid01, List } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { WeekCarousel } from '@/components/application/calendar/week-carousel';
import { WeekListView } from '@/components/application/calendar/week-list-view';
import { TrendCards } from '@/components/application/calendar/trend-cards';
import { WeeklyTargetsChart } from '@/components/application/calendar/weekly-targets-chart';
import { TargetEditModal } from '@/components/application/calendar/target-edit-modal';
import { containerService } from '@/data/mock-containers';
import { receiverTargetService } from '@/data/mock-receiver-targets';
import {
    getCurrentYear,
    getCurrentWeek,
    type WeekData,
    calculateYearlyAggregates,
    calculateYearlyTarget,
    calculateMonthlyAggregates,
    calculateMonthlyTarget,
    getMonthNumber,
} from '@/utils/week-utils';
import { useTranslation } from 'react-i18next';

export const ReceiverCalendarPage = () => {
    const { t } = useTranslation(['pages:calendar', 'common']);
    const [selectedYear, setSelectedYear] = useState(getCurrentYear());
    const [isLoading, setIsLoading] = useState(true);
    const [weeklyData, setWeeklyData] = useState<WeekData[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [viewType, setViewType] = useState<'containers' | 'revenue'>('containers');
    const [viewMode, setViewMode] = useState<'carousel' | 'list'>('carousel');
    const [editingWeek, setEditingWeek] = useState<number | null>(null);

    const currentWeek = getCurrentWeek();
    const currentYear = getCurrentYear();
    const currentMonth = getMonthNumber(new Date());

    // Simula il receiverId dell'utente corrente (in un'app reale verrebbe dal context)
    const receiverId = "2"; // Global Partners Corp

    // Carica i dati settimanali
    useEffect(() => {
        const loadWeeklyData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // Simula un piccolo delay per il caricamento
                await new Promise(resolve => setTimeout(resolve, 500));

                const data = containerService.getYearlyWeekData(receiverId, selectedYear);
                setWeeklyData(data);
            } catch (err) {
                setError(t('error'));
                console.error('Error loading weekly data:', err);
            } finally {
                setIsLoading(false);
            }
        };

        loadWeeklyData();
    }, [selectedYear, receiverId, t]);

    // Calcola aggregati e target
    const containers = useMemo(() => {
        return containerService.getByReceiverId(receiverId).filter(c => c.eta.getFullYear() === selectedYear);
    }, [receiverId, selectedYear]);

    const yearlyAggregates = useMemo(() => {
        return calculateYearlyAggregates(containers);
    }, [containers]);

    const defaultTarget = useMemo(() => {
        return receiverTargetService.getDefaultTarget(receiverId, selectedYear);
    }, [receiverId, selectedYear]);

    const yearlyTargets = useMemo(() => {
        return calculateYearlyTarget(defaultTarget);
    }, [defaultTarget]);

    const monthlyAggregates = useMemo(() => {
        return calculateMonthlyAggregates(containers);
    }, [containers]);

    const monthlyTargets = useMemo(() => {
        return calculateMonthlyTarget(defaultTarget, currentMonth, selectedYear);
    }, [defaultTarget, currentMonth, selectedYear]);

    const currentMonthData = useMemo(() => {
        return monthlyAggregates.find(m => m.month === currentMonth) || {
            month: currentMonth,
            totalContainers: 0,
            confirmedContainers: 0,
            pendingContainers: 0,
            totalRevenue: 0,
            confirmedRevenue: 0,
            pendingRevenue: 0,
        };
    }, [monthlyAggregates, currentMonth]);

    // Usa sempre tutte le 52 settimane (weeklyData già contiene tutte le settimane)
    const allWeeks = useMemo(() => {
        return weeklyData.sort((a, b) => a.weekNumber - b.weekNumber);
    }, [weeklyData]);

    const handlePreviousYear = () => {
        setSelectedYear(prev => prev - 1);
    };

    const handleNextYear = () => {
        setSelectedYear(prev => prev + 1);
    };

    const handleCurrentYear = () => {
        setSelectedYear(currentYear);
    };

    const handleEditTarget = (weekNumber: number) => {
        setEditingWeek(weekNumber);
    };

    const handleTargetSaved = () => {
        // Ricarica i dati per aggiornare i target
        const data = containerService.getYearlyWeekData(receiverId, selectedYear);
        setWeeklyData(data);
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                {/* Header Skeleton */}
                <div className="flex items-center justify-between">
                    <div className="space-y-2">
                        <div className="h-8 w-48 bg-secondary animate-pulse rounded"></div>
                        <div className="h-5 w-64 bg-secondary animate-pulse rounded"></div>
                    </div>
                    <div className="h-10 w-32 bg-secondary animate-pulse rounded"></div>
                </div>

                {/* Stats Skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-primary rounded-lg border border-secondary_alt p-4">
                            <div className="h-4 w-16 bg-secondary animate-pulse rounded mb-2"></div>
                            <div className="h-6 w-8 bg-secondary animate-pulse rounded"></div>
                        </div>
                    ))}
                </div>

                {/* Chart Skeleton */}
                <div className="bg-primary rounded-lg border border-secondary_alt p-6">
                    <div className="h-64 bg-secondary animate-pulse rounded"></div>
                </div>

                {/* Week Cards Skeleton */}
                <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="bg-primary rounded-lg border border-secondary_alt p-4">
                            <div className="h-6 w-32 bg-secondary animate-pulse rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <CalendarIcon className="h-12 w-12 text-tertiary mb-4" />
                <h3 className="text-lg font-medium text-primary mb-2">{t('error')}</h3>
                <Button onClick={() => window.location.reload()}>
                    {t('retry')}
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
                        <CalendarIcon className="h-6 w-6" />
                        {t('title')}
                    </h1>
                    <p className="text-tertiary mt-1">
                        {t('subtitle')}
                    </p>
                </div>

                {/* Year Navigation */}
                <div className="flex items-center gap-2">
                    <Button
                        color="secondary"
                        size="sm"
                        iconLeading={ChevronLeft}
                        onClick={handlePreviousYear}
                    >
                        {selectedYear - 1}
                    </Button>

                    <Button
                        color={selectedYear === currentYear ? "primary" : "secondary"}
                        size="sm"
                        onClick={handleCurrentYear}
                        className="min-w-[80px]"
                    >
                        {selectedYear}
                    </Button>

                    <Button
                        color="secondary"
                        size="sm"
                        iconTrailing={ChevronRight}
                        onClick={handleNextYear}
                    >
                        {selectedYear + 1}
                    </Button>
                </div>
            </div>

            {/* Trend Cards */}
            <TrendCards
                yearlyContainers={yearlyAggregates.totalContainers}
                yearlyRevenue={yearlyAggregates.totalRevenue}
                yearlyContainerTarget={yearlyTargets.containerTarget}
                yearlyRevenueTarget={yearlyTargets.revenueTarget}
                monthlyContainers={currentMonthData.totalContainers}
                monthlyRevenue={currentMonthData.totalRevenue}
                monthlyContainerTarget={monthlyTargets.containerTarget}
                monthlyRevenueTarget={monthlyTargets.revenueTarget}
            />

            {/* View Type Toggle */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button
                        color={viewType === 'containers' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setViewType('containers')}
                    >
                        {t('viewContainers')}
                    </Button>
                    <Button
                        color={viewType === 'revenue' ? 'primary' : 'secondary'}
                        size="sm"
                        onClick={() => setViewType('revenue')}
                    >
                        {t('viewRevenue')}
                    </Button>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2">
                    <Button
                        color={viewMode === 'carousel' ? 'primary' : 'secondary'}
                        size="sm"
                        iconLeading={LayoutGrid01}
                        onClick={() => setViewMode('carousel')}
                    >
                        {t('viewMode.carousel')}
                    </Button>
                    <Button
                        color={viewMode === 'list' ? 'primary' : 'secondary'}
                        size="sm"
                        iconLeading={List}
                        onClick={() => setViewMode('list')}
                    >
                        {t('viewMode.list')}
                    </Button>
                </div>
            </div>

            {/* Weekly Targets Chart */}
            <WeeklyTargetsChart
                weeklyData={weeklyData}
                receiverId={receiverId}
                year={selectedYear}
                type={viewType}
            />

            {/* Week View - Carousel or List */}
            {allWeeks.length === 0 ? (
                <div className="text-center py-12">
                    <CalendarIcon className="h-12 w-12 text-tertiary mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-primary mb-2">
                        {t('noContainersForYear', { year: selectedYear })}
                    </h3>
                    <p className="text-tertiary">
                        {t('noContainersMessage')}
                    </p>
                </div>
            ) : viewMode === 'carousel' ? (
                <WeekCarousel
                    weeks={allWeeks}
                    receiverId={receiverId}
                    year={selectedYear}
                    onEditTarget={handleEditTarget}
                />
            ) : (
                <WeekListView
                    weeks={allWeeks}
                    receiverId={receiverId}
                    year={selectedYear}
                    onEditTarget={handleEditTarget}
                />
            )}

            {/* Footer Info */}
            {allWeeks.length > 0 && (
                <div className="text-center text-sm text-tertiary py-4">
                    {t('viewingWeeks', { count: allWeeks.length, year: selectedYear })}
                    {selectedYear === currentYear && (
                        <span className="ml-2">• {t('currentWeek')}: {currentWeek}</span>
                    )}
                </div>
            )}

            {/* Target Edit Modal */}
            {editingWeek !== null && (
                <TargetEditModal
                    isOpen={editingWeek !== null}
                    onClose={() => setEditingWeek(null)}
                    receiverId={receiverId}
                    year={selectedYear}
                    weekNumber={editingWeek}
                    onSave={handleTargetSaved}
                />
            )}
        </div>
    );
};
